import { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Platform,
  Pressable,
  StyleSheet,
  View,
  Text,
} from 'react-native';
import { router } from 'expo-router';
import { observer } from 'mobx-react';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { useScale } from '@/hooks/useScale';
import { clearAccessToken } from '@/services/auth/authStorage';
import {
  clearAllCachedVideos,
  getCachedVideosSize,
} from '@/services/videoDownload';
import { useStores } from '@/stores/StoreProvider';
import { FocusGuide } from '@/components/FocusGuide';
import DefaultBackground from '@/components/DefaultBackground';
import TopbarLogo from '@/components/TopbarLogo';
import { formatBytes } from '@/utils/formatBytes';
import { getAppBuildNumber, getAppVersion } from '../../utils/appVersion';

export default observer(function SettingsScreen() {
  const styles = useStyles();
  const { userStore, guidedWorkoutPlayerStore } = useStores();
  const [cacheBytes, setCacheBytes] = useState<number | null>(null);
  const [isDeletingCache, setIsDeletingCache] = useState(false);

  const { currentUser } = userStore;

  useEffect(() => {
    let cancelled = false;
    getCachedVideosSize()
      .then((bytes) => {
        if (!cancelled) setCacheBytes(bytes);
      })
      .catch(() => {
        if (!cancelled) setCacheBytes(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const performDeleteVideos = async () => {
    console.log(
      `[Settings] Delete cache requested (was using ${
        cacheBytes !== null ? formatBytes(cacheBytes) : '?'
      })`,
    );
    setIsDeletingCache(true);
    try {
      guidedWorkoutPlayerStore.clearVideoCacheState();
      await clearAllCachedVideos();
      setCacheBytes(0);
      console.log('[Settings] Delete cache: done');
    } catch (err) {
      console.warn('[Settings] Failed to delete cache:', err);
      Alert.alert(
        'Could not delete cache',
        'Something went wrong. Please try again.',
      );
    } finally {
      setIsDeletingCache(false);
    }
  };

  const confirmDeleteVideos = () => {
    const title = 'Delete cache?';

    // The delete button only renders when cacheBytes > 0, so the size is
    // always known here. The fallback just keeps the copy sensible if that
    // ever stops being true.
    const message =
      cacheBytes !== null && cacheBytes > 0
        ? `This will free up ${formatBytes(cacheBytes)} of space. It won't affect your progress.`
        : "This will only free up space. It won't affect your progress.";

    // react-native-web's Alert.alert collapses to window.alert() and drops
    // the button callbacks, so the destructive action never fires. Use
    // window.confirm on web instead.
    if (Platform.OS === 'web') {
      if (
        typeof window !== 'undefined' &&
        window.confirm(`${title}\n\n${message}`)
      ) {
        performDeleteVideos();
      }
      return;
    }

    Alert.alert(title, message, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: performDeleteVideos,
      },
    ]);
  };

  const getAppInfo = (): string | null => {
    const version = getAppVersion();
    const buildNumber = getAppBuildNumber();

    const parts = [];

    if (version) {
      parts.push(`Version: ${version}`);
    }

    if (buildNumber) {
      parts.push(`Build: ${buildNumber}`);
    }

    if (parts.length === 0) {
      return null;
    }

    return parts.join(' | ');
  };

  const appInfo = getAppInfo();

  return (
    <ThemedView style={styles.container}>
      <DefaultBackground />

      {/* Top bar */}
      <FocusGuide autoFocus style={styles.topBarGuide}>
        <View style={styles.topBar}>
          <Pressable
            style={({ focused }) => [
              styles.backButton,
              focused && styles.backButtonFocused,
            ]}
            onPress={() => router.back()}
          >
            <ThemedText style={styles.backButtonText}>Back</ThemedText>
          </Pressable>
          <TopbarLogo />
        </View>
      </FocusGuide>

      {/* Title */}
      <ThemedText style={styles.title}>Settings</ThemedText>

      {/* User & App Info */}
      <View style={styles.userInfo}>
        {currentUser?.displayName ? (
          <ThemedText style={styles.userName}>
            {currentUser.displayName}
          </ThemedText>
        ) : null}
        {currentUser?.id ? (
          <ThemedText style={styles.infoText}>
            User ID: {currentUser.id}
          </ThemedText>
        ) : null}
        {currentUser?.email ? (
          <ThemedText style={styles.infoText}>{currentUser.email}</ThemedText>
        ) : null}
        {appInfo ? (
          <ThemedText style={styles.infoText}>{appInfo}</ThemedText>
        ) : null}
        <ThemedText style={styles.infoText}>
          Feedback/Questions - email{' '}
          <Text style={styles.boldText}>tv@hinote.com</Text>
        </ThemedText>
      </View>

      {/* Menu Items */}
      <FocusGuide autoFocus style={styles.contentGuide}>
        <View style={styles.content}>
          {currentUser?.canSeeDebugScreen ? (
            <Pressable
              style={({ focused }) => [
                styles.menuButton,
                focused && styles.menuButtonFocused,
              ]}
              onPress={() => router.push('/debug')}
            >
              <ThemedText style={styles.menuButtonText}>Debug</ThemedText>
            </Pressable>
          ) : null}

          {cacheBytes !== null && cacheBytes > 0 ? (
            <Pressable
              disabled={isDeletingCache}
              style={({ focused }) => [
                styles.menuButton,
                focused && styles.menuButtonFocused,
              ]}
              onPress={confirmDeleteVideos}
            >
              {isDeletingCache ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <ThemedText style={styles.menuButtonText}>
                  Delete cache
                </ThemedText>
              )}
            </Pressable>
          ) : null}

          <Pressable
            style={({ focused }) => [
              styles.menuButton,
              styles.dangerButton,
              focused && styles.dangerButtonFocused,
            ]}
            onPress={() => {
              userStore.clear();
              clearAccessToken();
            }}
          >
            <ThemedText style={styles.menuButtonText}>Logout</ThemedText>
          </Pressable>
        </View>
      </FocusGuide>
    </ThemedView>
  );
});

function useStyles() {
  const scale = useScale();

  return StyleSheet.create({
    container: {
      flex: 1,
    },
    topBarGuide: {
      width: '100%',
    },
    topBar: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 24 * scale,
      paddingHorizontal: 24 * scale,
    },
    backButton: {
      position: 'absolute',
      left: 24 * scale,
      paddingVertical: 8 * scale,
      paddingHorizontal: 20 * scale,
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderRadius: 20 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    backButtonFocused: {
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderColor: '#fff',
    },
    backButtonText: {
      color: '#fff',
      fontSize: 14 * scale,
      fontWeight: '600',
    },
    title: {
      color: '#fff',
      fontSize: 28 * scale,
      fontWeight: 'bold',
      textAlign: 'center',
      marginTop: 16 * scale,
      marginBottom: 32 * scale,
    },
    userInfo: {
      alignSelf: 'center',
      alignItems: 'center',
      marginBottom: 24 * scale,
    },
    userName: {
      color: '#fff',
      fontSize: 20 * scale,
      fontWeight: '700',
      textAlign: 'center',
    },
    infoText: {
      color: 'rgba(255,255,255,0.7)',
      fontSize: 14 * scale,
      textAlign: 'center',
      marginTop: 4 * scale,
    },
    boldText: {
      fontWeight: 'bold',
    },
    contentGuide: {
      width: '100%',
      alignItems: 'center',
    },
    content: {
      alignSelf: 'center',
      width: 400 * scale,
      gap: 12 * scale,
    },
    menuButton: {
      paddingVertical: 16 * scale,
      paddingHorizontal: 24 * scale,
      backgroundColor: 'rgba(255,255,255,0.12)',
      borderRadius: 12 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    menuButtonFocused: {
      backgroundColor: 'rgba(255,255,255,0.25)',
      borderColor: '#fff',
    },
    menuButtonText: {
      color: '#fff',
      fontSize: 18 * scale,
      fontWeight: '600',
      textAlign: 'center',
    },
    dangerButton: {
      backgroundColor: 'rgba(178, 41, 46, 1)',
    },
    dangerButtonFocused: {
      backgroundColor: 'rgba(178, 41, 46, 0.8)',
      borderColor: '#fff',
    },
  });
}
